import { useState, useEffect, useCallback } from "react";
import { useShownColorScheme } from "hooks/useShownColorScheme";
import {
  faArrowRotateRight,
  faBolt,
  faGaugeHigh,
  faHeartbeat,
  faPenFancy,
  faTemperatureThreeQuarters,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Box,
  Combobox,
  DefaultMantineColor,
  Group,
  InputBase,
  Popover,
  Text,
  useCombobox,
} from "@mantine/core";
import { ElevationIcon } from "assets/icons/ElevationIcon";
import { HotlineDataType } from "components/LeafletMap/TrackpointControls/HotlineControl";
import { Tooltip } from "components/UI/Tooltip";
import { useTrackStats } from "hooks/useTrackStats";
import { useTranslation } from "react-i18next";
import { useShowCesiumMap } from "stores/genericStore/GenericContext";
import { updateNonSaveableOptions } from "stores/optionsStore/actions";
import { useOptions } from "stores/optionsStore/OptionsContext";
import { useRouting } from "stores/routingStore/RoutingContext";
import { getYAxisOptions, SelectOption } from "utils/chart/yAxisOptions";
import { cleanObject } from "utils/helpers/cleanObject";
import { CollapsibleTopMenuActionIcon } from "../../../../components/UI/TopMenu/CollapsibleActionIcon";
import optionClasses from "components/UI/SelectableOption.module.css";

interface HotlineSelectOption {
  value: SelectOption["value"] | "none";
  label: string;
}

export const HotlineIcon = ({ isOpen }: { isOpen: boolean }) => {
  const { t } = useTranslation();
  const {
    dispatch,
    state: { hotlineType },
  } = useOptions();
  const {
    state: { trackWaypoints },
  } = useRouting();
  const showCesiumMap = useShowCesiumMap();
  const { stats } = useTrackStats();
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
    onDropdownOpen: (eventSource) => {
      if (eventSource === "keyboard") {
        combobox.selectActiveOption();
      } else {
        combobox.updateSelectedOptionIndex("active");
      }
    },
  });

  const [selectData, setSelectData] = useState<HotlineSelectOption[]>([
    { label: t("y_axis_options.none"), value: "none" },
  ]);
  const colorScheme = useShownColorScheme();
  const [filteredYAxixData, setFilteredYAxixData] = useState<string[]>();

  const handleSelect = (value: string | null) => {
    dispatch(
      updateNonSaveableOptions({ hotlineType: value as HotlineDataType })
    );
  };

  const setHotlineType = useCallback(
    (data: string[]) => {
      if (!data.includes(hotlineType))
        dispatch(updateNonSaveableOptions({ hotlineType: "none" }));
    },
    [dispatch, hotlineType]
  );

  useEffect(() => {
    if (stats?.chartData) {
      const yAxisData = stats?.chartData.map((item) => item.yAxis);
      const cleanedData = yAxisData.map((item) => cleanObject(item));
      const keys = Object.keys(cleanedData[cleanedData.length - 1]);
      setHotlineType(keys);
      setFilteredYAxixData(keys);
      const yAxisOptions = getYAxisOptions(t, keys);
      setSelectData([
        { label: t("y_axis_options.none"), value: "none" },
        ...yAxisOptions,
      ]);
    }
  }, [stats, t, setHotlineType]);

  /** Change icons for individual hotline types */
  const getHotlineIcon = useCallback(
    (color?: DefaultMantineColor, iconType?: string) => {
      const type = iconType ?? hotlineType;
      const getColorSchemeByTheme = () =>
        colorScheme === "light" ? "#0b0b0b" : "#b7b8bb";

      if (type === "elevation")
        return (
          <Box style={{ color: color ?? getColorSchemeByTheme() }}>
            <ElevationIcon />
          </Box>
        );
      if (type === "speed")
        return (
          <FontAwesomeIcon
            color={color ?? getColorSchemeByTheme()}
            icon={faGaugeHigh}
          />
        );
      if (type === "cadence")
        return (
          <FontAwesomeIcon
            color={color ?? getColorSchemeByTheme()}
            icon={faArrowRotateRight}
          />
        );
      if (type === "heartRate")
        return (
          <FontAwesomeIcon
            color={color ?? getColorSchemeByTheme()}
            icon={faHeartbeat}
          />
        );
      if (type === "power")
        return (
          <FontAwesomeIcon
            color={color ?? getColorSchemeByTheme()}
            icon={faBolt}
          />
        );
      if (type === "temperature")
        return (
          <FontAwesomeIcon
            color={color ?? getColorSchemeByTheme()}
            icon={faTemperatureThreeQuarters}
          />
        );
      return "";
    },
    [hotlineType, colorScheme]
  );

  return (
    <Popover position="bottom" withinPortal trapFocus>
      <Tooltip label={t("editmode.hotline_button_tooltip")}>
        <Popover.Target>
          <div>
            <CollapsibleTopMenuActionIcon
              isOpen={isOpen}
              active={
                hotlineType !== "none" &&
                filteredYAxixData?.includes(hotlineType)
              }
              disabled={showCesiumMap || trackWaypoints.length < 2}
            >
              {hotlineType !== "none" &&
              filteredYAxixData?.includes(hotlineType) ? (
                getHotlineIcon("")
              ) : (
                <FontAwesomeIcon icon={faPenFancy} />
              )}
            </CollapsibleTopMenuActionIcon>
          </div>
        </Popover.Target>
      </Tooltip>
      <Popover.Dropdown>
        <Combobox
          store={combobox}
          resetSelectionOnOptionHover
          withinPortal={false}
          onOptionSubmit={(value) => {
            handleSelect(value);
            combobox.updateSelectedOptionIndex("active");
            combobox.closeDropdown();
          }}
        >
          <Combobox.Target targetType="button">
            <InputBase
              component="button"
              type="button"
              pointer
              // Fits the usual labels (e.g. "Frequência cardíaca"), longer ones still widen it.
              // The dropdown list takes the same width.
              miw={240}
              rightSection={<Combobox.Chevron />}
              rightSectionPointerEvents="none"
              onClick={() => combobox.toggleDropdown()}
            >
              <Group wrap="nowrap">
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                    width: "100%",
                  }}
                >
                  {hotlineType !== "none" ? (
                    <span style={{ width: "0.75rem" }}>{getHotlineIcon()}</span>
                  ) : null}
                  <Text size="sm">
                    {
                      selectData.find((item) => item.value === hotlineType)
                        ?.label
                    }
                  </Text>
                </div>
              </Group>
            </InputBase>
          </Combobox.Target>
          <Combobox.Dropdown>
            <Combobox.Options>
              {selectData.map(({ value, label }) => {
                const isSelected = hotlineType === value;

                return (
                  <Combobox.Option
                    className={optionClasses.option}
                    mod={{ selected: isSelected }}
                    value={value}
                    key={value}
                  >
                    <Group wrap="nowrap">
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.75rem",
                          width: "100%",
                        }}
                      >
                        {value !== "none" ? (
                          <span style={{ width: "0.75rem" }}>
                            {getHotlineIcon(undefined, value)}
                          </span>
                        ) : null}
                        <Text size="sm">{label}</Text>
                      </div>
                    </Group>
                  </Combobox.Option>
                );
              })}
            </Combobox.Options>
          </Combobox.Dropdown>
        </Combobox>
      </Popover.Dropdown>
    </Popover>
  );
};
